import React from "react"

import {Theme} from "@bdfx/mergado-ui-kit";
import {EraserLineType, ObjectsType, PaintLineType, UserDefinedObjectType} from "./watermark-marker-common";
import { InpaintingGroupType, PositionType } from "products/types";
import { invertColor } from "app/helpers/other";
import { CoordsType } from "shape-entities/components/shape";

const globalCompositeOperation = 'source-over'              //source-over, xor
const globalCompositeOperationEraser = 'destination-out'    //destination-out

const { abs, sin, cos, PI, max, min, round, atan2, atan, sqrt, floor, pow } = Math

type SizeAndPositionType = {
    width: number
    height: number
    middleX: number
    middleY: number
}

export const WatermarksRenderCanvasObjects = (objects: ObjectsType, color : string = Theme.steelblue, canvas: HTMLCanvasElement, opacity : number = .75) => {
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.globalAlpha = opacity;
    ctx.imageSmoothingEnabled = false;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    {objects && Array.isArray(objects) &&
        objects.map((object: (PaintLineType | EraserLineType), key: number) => {
            //not points nor eraser?
            if ((!object['points'] || !Array.isArray(object['points'])) && (!object['eraser'] || !Array.isArray(object['eraser']))) {
                return;
            }

            //check the current type:
            const currentObjectType: UserDefinedObjectType = typeof object['points'] !== "undefined" ? 'points' : typeof object['eraser'] !== "undefined" ? 'eraser' : null
            const isEraser = currentObjectType === 'eraser'

            ctx.beginPath()
            if (object[currentObjectType] && Array.isArray(object[currentObjectType])) {
                ctx.globalCompositeOperation = isEraser ? globalCompositeOperationEraser : globalCompositeOperation
                ctx.globalAlpha = isEraser ? 1 : opacity;
                ctx.lineWidth = 2 * object.radius;

                if (object[currentObjectType].length === 1) {
                    ctx.arc(
                        object[currentObjectType][0].x,
                        object[currentObjectType][0].y,
                        object.radius,
                        0,
                        PI * 2,
                        false
                    );
                    ctx.fill()
                } else {
                    object[currentObjectType].map((point, key) => {
                        if (key === 0) {
                            ctx.moveTo(round(point.x), round(point.y));
                        } else {
                            ctx.lineTo(round(point.x), round(point.y));
                        }
                    })
                    ctx.stroke();
                }
            }

            ctx.closePath()
        })
    }
}

export const getObjectsSizeAndPosition = (objects: ObjectsType): SizeAndPositionType => {
    let maxX = null, maxY = null, minX = null, minY = null
    if (objects && Array.isArray(objects)) {
        objects.forEach((object: (PaintLineType | EraserLineType), key: number) => {
            if ((!object['points'] || !Array.isArray(object['points'])) && (!object['eraser'] || !Array.isArray(object['eraser']))) {
                return;
            }

            const currentObjectType: UserDefinedObjectType = typeof object['points'] !== "undefined" ? 'points' : typeof object['eraser'] !== "undefined" ? 'eraser' : null
            const isEraser = currentObjectType === 'eraser'

            if (object[currentObjectType] && Array.isArray(object[currentObjectType])) {
                const r = object.radius ? object.radius : 1
                if (!isEraser) {
                    object[currentObjectType].forEach((point, key) => {
                        if (maxX === null || maxX < (point.x + r)) {
                            maxX = (point.x + r)
                        }
                        if (maxY === null || maxY < (point.y + r)) {
                            maxY = (point.y + r)
                        }
                        if (minX === null || minX > (point.x - r)) {
                            minX = (point.x - r)
                        }
                        if (minY === null || minY > (point.y - r)) {
                            minY = (point.y - r)
                        }
                    })
                }
            }
        })

        return {
            width: abs(maxX - minX),
            height: abs(maxY - minY),
            middleX: (maxX + minX) / 2,
            middleY: (maxY + minY) / 2,
        }
    }

    return null
}

export const DrawDimensionLines = (objects: ObjectsType, color: string, backgroundCanvas: HTMLCanvasElement, opacity: number, posRelativeTo: PositionType) => {
    const iColor = invertColor(color)
    const radius = 10

    const sizeAndPosition = getObjectsSizeAndPosition(objects)

    const { width, height, middleX, middleY } = sizeAndPosition

    const ctx = backgroundCanvas.getContext("2d");
    ctx.clearRect(0, 0, backgroundCanvas.width, backgroundCanvas.height);

    if (sizeAndPosition) {
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.imageSmoothingEnabled = true;
        ctx.globalCompositeOperation = globalCompositeOperation

        ctx.strokeStyle = iColor
        ctx.fillStyle = iColor

        ctx.globalAlpha = .25
        ctx.lineWidth = 1

        ctx.beginPath()
        ctx.rect(middleX - width / 2, middleY - height / 2, width, height);
        ctx.stroke()

        ctx.strokeStyle = color
        ctx.fillStyle = color

        ctx.moveTo(middleX, middleY);
        ctx.arc(middleX, middleY, radius, 0, PI * 2, false)
        ctx.stroke()

        ctx.strokeStyle = iColor
        ctx.fillStyle = iColor
        ctx.globalAlpha = opacity
        ctx.lineWidth = 3
        ctx.setLineDash([4, 1])

        ctx.moveTo(middleX, middleY);
        switch (posRelativeTo) {
            case 'topleft':
                ctx.lineTo(0, 0);
                ctx.arc(0, 0, radius, 0, PI * 2, false)
                break;
            case 'topcenter':
                ctx.lineTo(backgroundCanvas.width / 2, 0);
                ctx.arc(backgroundCanvas.width / 2, 0, radius, 0, PI * 2, false)
                break;
            case 'topright':
                ctx.lineTo(backgroundCanvas.width, 0);
                ctx.arc(backgroundCanvas.width, 0, radius, 0, PI * 2, false)
                break;
            case 'leftcenter':
                ctx.lineTo(0, backgroundCanvas.height / 2);
                ctx.arc(0, backgroundCanvas.height / 2, radius, 0, PI * 2, false)
                break;
            case 'center':
                ctx.lineTo(backgroundCanvas.width / 2, backgroundCanvas.height / 2);
                ctx.arc(backgroundCanvas.width / 2, backgroundCanvas.height / 2, radius, 0, PI * 2, false)
                break;
            case 'rightcenter':
                ctx.lineTo(backgroundCanvas.width, backgroundCanvas.height / 2);
                ctx.arc(backgroundCanvas.width, backgroundCanvas.height / 2, radius, 0, PI * 2, false)
                break;
            case 'bottomleft':
                ctx.lineTo(0, backgroundCanvas.height);
                ctx.arc(0, backgroundCanvas.height, radius, 0, PI * 2, false)
                break;
            case 'bottomcenter':
                ctx.lineTo(backgroundCanvas.width / 2, backgroundCanvas.height);
                ctx.arc(backgroundCanvas.width / 2, backgroundCanvas.height, radius, 0, PI * 2, false)
                break;
            case 'bottomright':
                ctx.lineTo(backgroundCanvas.width, backgroundCanvas.height);
                ctx.arc(backgroundCanvas.width, backgroundCanvas.height, radius, 0, PI * 2, false)
                break;
        }

        ctx.stroke();
        ctx.closePath()
    }
}

export const WatermarksRenderSvgObjects = ({ objects, color = Theme.steelblue, opacity = .5}) => (
    <g style={{ opacity: opacity, pointerEvents: 'none' }} id={'semitransparent'}>
        {objects && Array.isArray(objects) &&
            objects.map((object: (PaintLineType | EraserLineType), key) => {
                //not points nor eraser?
                if ((!object['points'] || !Array.isArray(object['points'])) && (!object['eraser'] || !Array.isArray(object['eraser']))) {
                    return <g key={`paint-${key}`} />
                }

                //check the current type:
                const currentObjectType: UserDefinedObjectType = typeof object['points'] !== "undefined" ? 'points' : typeof object['eraser'] !== "undefined" ? 'eraser' : null
                const colorByType = currentObjectType === 'eraser' ? 'white' : color

                if (object[currentObjectType] && Array.isArray(object[currentObjectType]) && object[currentObjectType].length === 1) {
                    return <circle key={`paint-point-${key}`} cx={object[currentObjectType][0].x} cy={object[currentObjectType][0].y} r={object.radius} fill={colorByType} />
                }

                const points = object[currentObjectType].map(point => `${round(point.x)},${round(point.y)}`).join(' ')
                return <g key={`paint-line-${key}`}>
                    <polyline
                        points={points}
                        fill="none"
                        stroke={colorByType}
                        strokeWidth={2 * object.radius}
                        strokeLinejoin={'round'}
                        strokeLinecap={'round'}
                    />
                </g>
            })
        }
    </g>
)

export const recalculateObjects = (inpaintingGroup: InpaintingGroupType, imageWidth: number, imageHeight: number): string => {
    if (
        inpaintingGroup && inpaintingGroup.user_set_objects && imageWidth && imageHeight &&
        (imageWidth !== inpaintingGroup.width || imageHeight !== inpaintingGroup.height)
    ) {
        const objects = JSON.parse(inpaintingGroup.user_set_objects)
        const dw = imageWidth - inpaintingGroup.width
        const dh = imageHeight - inpaintingGroup.height

        let dx = 0, dy = 0
        if ((imageWidth !== inpaintingGroup.width || imageHeight !== inpaintingGroup.height)) {
            switch (inpaintingGroup.pos_relative_to) {
                case 'topcenter':
                    dx = dw / 2
                    dy = 0
                    break;
                case 'topright':
                    dx = dw
                    dy = 0
                    break;
                case 'leftcenter':
                    dx = 0
                    dy = dh / 2
                    break;
                case 'center':
                    dx = dw / 2
                    dy = dh / 2
                    break;
                case 'rightcenter':
                    dx = 0
                    dy = dh / 2
                    break;
                case 'bottomleft':
                    dx = 0
                    dy = dh
                    break;
                case 'bottomcenter':
                    dx = dw / 2
                    dy = dh
                    break;
                case 'bottomright':
                    dx = dw
                    dy = dh
                    break;
                default:
                case null:
                case 'topleft':
                    dx = 0
                    dy = 0
                    break;
            }


            const scaleX = imageWidth / inpaintingGroup.width
            const scaleY = imageHeight / inpaintingGroup.height

            const avgScale = (scaleX + scaleY) / 2

            const centeredObjects = objects.map((object: (PaintLineType | EraserLineType)) => {
                const radius = inpaintingGroup.scale_objects ? object['radius'] * avgScale : object['radius']

                const updatedObject = {
                    ...object,
                    radius: radius,
                    points: typeof object['points'] !== 'undefined' ? object['points'].map((point: CoordsType) => {
                        return {
                            ...point,
                            x: inpaintingGroup.pos_relative_to ? (point.x + dx) : (point.x + dx) * scaleX,
                            y: inpaintingGroup.pos_relative_to ? (point.y + dy) : (point.y + dy) * scaleY
                        }
                    }) : undefined,
                    eraser: typeof object['eraser'] !== 'undefined' ? object['eraser'].map((point: CoordsType) => {
                        return {
                            ...point,
                            x: inpaintingGroup.pos_relative_to ? (point.x + dx) : (point.x + dx) * scaleX,
                            y: inpaintingGroup.pos_relative_to ? (point.y + dy) : (point.y + dy) * scaleY
                        }
                    }) : undefined,
                }

                return updatedObject
            })

            return JSON.stringify(centeredObjects)
        }
    }

    return inpaintingGroup?.user_set_objects
}